import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Autocomplete,
  Chip,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Lock as LockIcon } from '@mui/icons-material';
import {
  createTask,
  updateTask,
  createDeliverable,
  updateDeliverable,
  deleteDeliverable,
  fetchDeliverables,
  createObjective,
  updateObjective,
  deleteObjective,
  fetchObjectives,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  fetchMilestones,
  createRisk,
  updateRisk,
  deleteRisk,
  fetchRisks
} from 'shared/utils/proposalApi';
import { ProfileDatePicker } from 'components/social/profile/ProfileDialogs';

// Normalize integer-or-empty fields to null before sending to the API.
// DRF IntegerFields reject empty strings even when null=True.
const intOrNull = v => (v !== '' && v != null ? parseInt(v) : null);

const TaskDialog = ({ open, onClose, proposalId, workPackages, sections, task, onSuccess, consortiumMembers, defaultWorkPackageId, canEditAssignees = true }) => {
  const isEdit = Boolean(task);

  // ── Core fields ──────────────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [workPackage, setWorkPackage] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [taskLeader, setTaskLeader] = useState(null);

  // ── Number (shown on both create and edit) ───────────────────────────────────
  const [number, setNumber] = useState('');

  // ── Edit-only fields ─────────────────────────────────────────────────────────
  const [section, setSection] = useState('');
  const [taskStatus, setTaskStatus] = useState('pending');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [durationMonths, setDurationMonths] = useState('');
  const [description, setDescription] = useState('');

  // ── Sub-entities (edit only) ──────────────────────────────────────────────
  const [deliverables, setDeliverables] = useState([]);
  const [deliverableForm, setDeliverableForm] = useState(emptyDeliverable());
  const [editingDeliverableIndex, setEditingDeliverableIndex] = useState(null);
  const [showDeliverableForm, setShowDeliverableForm] = useState(false);

  const [objectives, setObjectives] = useState([]);
  const [objectiveForm, setObjectiveForm] = useState(emptyObjective());
  const [editingObjectiveIndex, setEditingObjectiveIndex] = useState(null);
  const [showObjectiveForm, setShowObjectiveForm] = useState(false);

  const [milestones, setMilestones] = useState([]);
  const [milestoneForm, setMilestoneForm] = useState(emptyMilestone());
  const [editingMilestoneIndex, setEditingMilestoneIndex] = useState(null);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);

  const [risks, setRisks] = useState([]);
  const [riskForm, setRiskForm] = useState(emptyRisk());
  const [editingRiskIndex, setEditingRiskIndex] = useState(null);
  const [showRiskForm, setShowRiskForm] = useState(false);

  const [users, setUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Tracks IDs of sub-entities that were reassigned to other tasks this session.
  // saveDeliverables/etc must NOT delete these — they're still in the DB, just under a different task.
  const reassignedIds = useRef(new Set());

  // Flat list of all tasks across all WPs — for reassignment dropdowns
  const allTasks = useMemo(() =>
    (workPackages || []).flatMap(wp =>
      (wp.tasks || []).map(t => ({ ...t, wp_title: wp.title, wp_number: wp.number }))
    ),
    [workPackages]
  );

  // ── Auto-increment number for create ─────────────────────────────────────────
  useEffect(() => {
    if (!open || isEdit) return;
    const wpId = workPackage || defaultWorkPackageId || workPackages?.[0]?.id;
    const wp = workPackages?.find(w => w.id === wpId);
    const tasks = wp?.tasks || [];
    const maxNum = tasks.length > 0 ? Math.max(...tasks.map(t => t.number || 0)) : 0;
    setNumber(String(maxNum + 1));
  }, [open, workPackage, isEdit, workPackages, defaultWorkPackageId]);

  // ── Populate from task prop ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    reassignedIds.current = new Set(); // reset on every open
    setError(null);
    if (task) {
      setTitle(task.title || '');
      setWorkPackage(task.work_package || '');
      setNumber(task.number != null ? String(task.number) : '');
      setSection(task.section || '');
      setTaskStatus(task.status || 'pending');
      setPriority(task.priority || 'medium');
      setDueDate(task.due_date || '');
      setStartMonth(task.start_month || '');
      setDurationMonths(task.duration_months || '');
      setDescription(task.description || '');
    } else {
      setTitle('');
      setWorkPackage(defaultWorkPackageId || workPackages?.[0]?.id || '');
      setSection('');
      setTaskStatus('pending');
      setPriority('medium');
      setDueDate('');
      setStartMonth('');
      setDurationMonths('');
      setDescription('');
      setAssignees([]);
      resetSubEntities();
    }
  }, [open, task, defaultWorkPackageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load users ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const valid = (consortiumMembers || []).filter(u => u?.id);
    setUsers(valid);
    if (task?.assignees?.length) {
      setAssignees(task.assignees.map(id => valid.find(u => u.id === id)).filter(Boolean));
    }
    setTaskLeader(task?.task_leader ? valid.find(u => u.id === task.task_leader) || null : null);
  }, [open, consortiumMembers, task]);

  // ── Load sub-entities when editing ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    resetSubEntities();
    if (task?.id) {
      fetchDeliverables(task.id).then(r => r?.deliverables && setDeliverables(r.deliverables));
      fetchObjectives(task.id).then(r => r?.objectives && setObjectives(r.objectives));
      fetchMilestones(task.id).then(r => r?.milestones && setMilestones(r.milestones));
      fetchRisks(task.id).then(r => r?.risks && setRisks(r.risks));
    }
  }, [open, task]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetSubEntities() {
    setDeliverables([]); setDeliverableForm(emptyDeliverable()); setShowDeliverableForm(false); setEditingDeliverableIndex(null);
    setObjectives([]); setObjectiveForm(emptyObjective()); setShowObjectiveForm(false); setEditingObjectiveIndex(null);
    setMilestones([]); setMilestoneForm(emptyMilestone()); setShowMilestoneForm(false); setEditingMilestoneIndex(null);
    setRisks([]); setRiskForm(emptyRisk()); setShowRiskForm(false); setEditingRiskIndex(null);
  }

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!title.trim() || !workPackage) return;
    setIsSaving(true);
    setError(null);

    const submitData = {
      proposal: proposalId,
      title: title.trim(),
      work_package: workPackage,
      number: number ? parseInt(number) : null,
      assignees: assignees.map(u => u.id),
      task_leader: taskLeader?.id || null,
      ...(isEdit && {
        section: section || null,
        status: taskStatus,
        priority,
        due_date: dueDate || null,
        start_month: intOrNull(startMonth),
        duration_months: intOrNull(durationMonths),
        description: description || null,
      }),
    };

    const result = isEdit
      ? await updateTask(task.id, submitData)
      : await createTask(submitData);

    if (result.success) {
      const taskId = task?.id || result.data.id;
      if (isEdit) {
        // Flush any un-persisted sub-entities (those without an id were locally added
        // but immediate-save failed or the user was offline)
        await saveDeliverables(taskId);
        await saveObjectives(taskId);
        await saveMilestones(taskId);
        await saveRisks(taskId);
      }
      onSuccess?.(result.data);
      onClose();
    } else {
      setError(result.error);
    }
    setIsSaving(false);
  };

  // ── Deliverable handlers ──────────────────────────────────────────────────────
  const handleAddDeliverable = async () => {
    if (!deliverableForm.description?.trim()) return;
    const { number: _n, taskId: formTaskId, ...rest } = deliverableForm;

    if (editingDeliverableIndex !== null) {
      const updated = [...deliverables];
      updated[editingDeliverableIndex] = { ...deliverableForm };
      if (deliverableForm.id) {
        const targetTask = formTaskId || task?.id;
        const data = {
          ...rest,
          task: targetTask,
          assignee: deliverableForm.assignee?.id ?? deliverableForm.assignee ?? null,
          delivery_month: intOrNull(rest.delivery_month),
        };
        await updateDeliverable(deliverableForm.id, data);
        // If task changed, remove from local list (it now belongs to another task)
        if (formTaskId && formTaskId !== task?.id) {
          reassignedIds.current.add(deliverableForm.id);
          setDeliverables(prev => prev.filter((_, i) => i !== editingDeliverableIndex));
          setEditingDeliverableIndex(null);
          setDeliverableForm(emptyDeliverable());
          setShowDeliverableForm(false);
          return;
        }
      }
      setDeliverables(updated);
      setEditingDeliverableIndex(null);
    } else {
      // Adding new — immediately persist if we have a task id
      if (task?.id) {
        const data = {
          ...rest,
          task: task.id,
          assignee: deliverableForm.assignee?.id ?? deliverableForm.assignee ?? null,
          delivery_month: intOrNull(rest.delivery_month),
        };
        const result = await createDeliverable(data);
        setDeliverables(prev => [...prev, result.success ? result.data : { ...deliverableForm }]);
      } else {
        setDeliverables(prev => [...prev, { ...deliverableForm }]);
      }
    }
    setDeliverableForm(emptyDeliverable());
    setShowDeliverableForm(false);
  };

  const saveDeliverables = async (taskId) => {
    if (task?.deliverables) {
      const kept = deliverables.filter(d => d.id).map(d => d.id);
      for (const d of task.deliverables.filter(d => !kept.includes(d.id) && !reassignedIds.current.has(d.id))) {
        await deleteDeliverable(d.id);
      }
    }
    // Only flush items that still have no id (weren't immediately saved)
    for (const d of deliverables.filter(d => !d.id)) {
      const { number: _n, taskId: _tid, ...rest } = d;
      const data = {
        ...rest,
        task: taskId,
        assignee: d.assignee?.id ?? d.assignee ?? null,
        delivery_month: intOrNull(rest.delivery_month),
      };
      await createDeliverable(data);
    }
  };

  // ── Objective handlers ────────────────────────────────────────────────────────
  const handleAddObjective = async () => {
    if (!objectiveForm.description?.trim()) return;
    const { number: _n, taskId: formTaskId, ...rest } = objectiveForm;

    if (editingObjectiveIndex !== null) {
      const updated = [...objectives];
      updated[editingObjectiveIndex] = { ...objectiveForm };
      if (objectiveForm.id) {
        const targetTask = formTaskId || task?.id;
        const data = { ...rest, task: targetTask };
        await updateObjective(objectiveForm.id, data);
        if (formTaskId && formTaskId !== task?.id) {
          reassignedIds.current.add(objectiveForm.id);
          setObjectives(prev => prev.filter((_, i) => i !== editingObjectiveIndex));
          setEditingObjectiveIndex(null);
          setObjectiveForm(emptyObjective());
          setShowObjectiveForm(false);
          return;
        }
      }
      setObjectives(updated);
      setEditingObjectiveIndex(null);
    } else {
      if (task?.id) {
        const data = { ...rest, task: task.id };
        const result = await createObjective(data);
        setObjectives(prev => [...prev, result.success ? result.data : { ...objectiveForm }]);
      } else {
        setObjectives(prev => [...prev, { ...objectiveForm }]);
      }
    }
    setObjectiveForm(emptyObjective());
    setShowObjectiveForm(false);
  };

  const saveObjectives = async (taskId) => {
    if (task?.objectives) {
      const kept = objectives.filter(o => o.id).map(o => o.id);
      for (const o of task.objectives.filter(o => !kept.includes(o.id) && !reassignedIds.current.has(o.id))) {
        await deleteObjective(o.id);
      }
    }
    for (const o of objectives.filter(o => !o.id)) {
      const { number: _n, taskId: _tid, ...rest } = o;
      await createObjective({ ...rest, task: taskId });
    }
  };

  // ── Milestone handlers ────────────────────────────────────────────────────────
  const handleAddMilestone = async () => {
    if (!milestoneForm.description?.trim()) return;
    const { number: _n, taskId: formTaskId, ...rest } = milestoneForm;

    if (editingMilestoneIndex !== null) {
      const updated = [...milestones];
      updated[editingMilestoneIndex] = { ...milestoneForm };
      if (milestoneForm.id) {
        const targetTask = formTaskId || task?.id;
        const data = { ...rest, task: targetTask, due_month: intOrNull(rest.due_month) };
        await updateMilestone(milestoneForm.id, data);
        if (formTaskId && formTaskId !== task?.id) {
          reassignedIds.current.add(milestoneForm.id);
          setMilestones(prev => prev.filter((_, i) => i !== editingMilestoneIndex));
          setEditingMilestoneIndex(null);
          setMilestoneForm(emptyMilestone());
          setShowMilestoneForm(false);
          return;
        }
      }
      setMilestones(updated);
      setEditingMilestoneIndex(null);
    } else {
      if (task?.id) {
        const data = { ...rest, task: task.id, due_month: intOrNull(rest.due_month) };
        const result = await createMilestone(data);
        setMilestones(prev => [...prev, result.success ? result.data : { ...milestoneForm }]);
      } else {
        setMilestones(prev => [...prev, { ...milestoneForm }]);
      }
    }
    setMilestoneForm(emptyMilestone());
    setShowMilestoneForm(false);
  };

  const saveMilestones = async (taskId) => {
    if (task?.milestones) {
      const kept = milestones.filter(m => m.id).map(m => m.id);
      for (const m of task.milestones.filter(m => !kept.includes(m.id) && !reassignedIds.current.has(m.id))) {
        await deleteMilestone(m.id);
      }
    }
    for (const m of milestones.filter(m => !m.id)) {
      const { number: _n, taskId: _tid, ...rest } = m;
      await createMilestone({ ...rest, task: taskId, due_month: intOrNull(rest.due_month) });
    }
  };

  // ── Risk handlers ─────────────────────────────────────────────────────────────
  const handleAddRisk = async () => {
    if (!riskForm.description?.trim()) return;
    const { number: _n, taskId: formTaskId, ...rest } = riskForm;

    if (editingRiskIndex !== null) {
      const updated = [...risks];
      updated[editingRiskIndex] = { ...riskForm };
      if (riskForm.id) {
        const targetTask = formTaskId || task?.id;
        const data = { ...rest, task: targetTask };
        await updateRisk(riskForm.id, data);
        if (formTaskId && formTaskId !== task?.id) {
          reassignedIds.current.add(riskForm.id);
          setRisks(prev => prev.filter((_, i) => i !== editingRiskIndex));
          setEditingRiskIndex(null);
          setRiskForm(emptyRisk());
          setShowRiskForm(false);
          return;
        }
      }
      setRisks(updated);
      setEditingRiskIndex(null);
    } else {
      if (task?.id) {
        const data = { ...rest, task: task.id };
        const result = await createRisk(data);
        setRisks(prev => [...prev, result.success ? result.data : { ...riskForm }]);
      } else {
        setRisks(prev => [...prev, { ...riskForm }]);
      }
    }
    setRiskForm(emptyRisk());
    setShowRiskForm(false);
  };

  const saveRisks = async (taskId) => {
    if (task?.risks) {
      const kept = risks.filter(r => r.id).map(r => r.id);
      for (const r of task.risks.filter(r => !kept.includes(r.id) && !reassignedIds.current.has(r.id))) {
        await deleteRisk(r.id);
      }
    }
    for (const r of risks.filter(r => !r.id)) {
      const { number: _n, taskId: _tid, ...rest } = r;
      await createRisk({ ...rest, task: taskId });
    }
  };

  // ── Task dropdown (for reassigning sub-entities) ──────────────────────────────
  const TaskReassignSelect = ({ value, onChange }) => (
    <FormControl fullWidth size="small">
      <InputLabel>Task</InputLabel>
      <Select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        label="Task"
        MenuProps={{ sx: { zIndex: 3001 } }}
      >
        {allTasks.map(t => (
          <MenuItem key={t.id} value={t.id}>
            {t.full_title || `T${t.wp_number}.${t.number} – ${t.title}`}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({t.wp_title})
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle>{isEdit ? 'Edit Task' : 'Add Task'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>

          {/* ── Always shown ── */}
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            autoFocus
            fullWidth
          />

          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth required>
              <InputLabel>Work Package</InputLabel>
              <Select
                value={workPackage}
                onChange={e => setWorkPackage(e.target.value)}
                label="Work Package"
                MenuProps={{ sx: { zIndex: 3001 } }}
              >
                {workPackages?.map(wp => (
                  <MenuItem key={wp.id} value={wp.id}>{wp.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Task number — auto-incremented on create, editable always */}
            <TextField
              label="Task Number"
              type="number"
              value={number}
              onChange={e => setNumber(e.target.value)}
              helperText={isEdit ? 'Change if needed' : 'Auto-incremented — adjust if needed'}
              sx={{ flex: '0 0 200px' }}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              {canEditAssignees ? (
                <Autocomplete
                  multiple
                  options={users}
                  value={assignees}
                  slotProps={{ popper: { style: { zIndex: 3001 } } }}
                  onChange={(_, v) => setAssignees(v)}
                  getOptionLabel={opt => {
                    if (!opt) return '';
                    const name = opt.name || opt.email || 'Unknown';
                    return opt.org_short_name ? `${name} (${opt.org_short_name})` : name;
                  }}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Assignees"
                      placeholder="Select team members"
                      helperText="Budget and person-month entries are restricted to task assignees."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, i) => (
                      <Chip key={opt?.id || i} label={opt?.name || opt?.email || 'Unknown'}
                        {...getTagProps({ index: i })} size="small" />
                    ))
                  }
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LockIcon sx={{ fontSize: 13 }} />
                    Assignees — editable by the WP leader or consortium coordinator
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5, minHeight: 32, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                    {assignees.length > 0
                      ? assignees.map(opt => (
                          <Chip key={opt?.id} label={opt?.name || opt?.email || 'Unknown'} size="small" variant="outlined" />
                        ))
                      : <Typography variant="caption" color="text.disabled">No assignees set</Typography>
                    }
                  </Stack>
                </Box>
              )}
            </Box>

            <Autocomplete
              options={users}
              value={taskLeader}
              slotProps={{ popper: { style: { zIndex: 3001 } } }}
              onChange={(_, v) => setTaskLeader(v)}
              getOptionLabel={opt => {
                if (!opt) return '';
                const name = opt.name || opt.email || 'Unknown';
                return opt.org_short_name ? `${name} (${opt.org_short_name})` : name;
              }}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              sx={{ minWidth: 220 }}
              renderInput={params => (
                <TextField {...params} label="Task Leader" placeholder="Select leader" />
              )}
            />
          </Stack>

          {/* ── Edit-only fields ── */}
          {isEdit && (
            <>
              <Divider />

              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Related Section (Optional)</InputLabel>
                  <Select
                    value={section}
                    onChange={e => setSection(e.target.value)}
                    label="Related Section (Optional)"
                    MenuProps={{ sx: { zIndex: 3001 } }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {sections?.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={taskStatus} onChange={e => setTaskStatus(e.target.value)} label="Status" MenuProps={{ sx: { zIndex: 3001 } }}>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select value={priority} onChange={e => setPriority(e.target.value)} label="Priority" MenuProps={{ sx: { zIndex: 3001 } }}>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <ProfileDatePicker
                  label="Due Date"
                  value={dueDate}
                  onChange={setDueDate}
                />
                <TextField
                  label="Start Month"
                  type="number"
                  value={startMonth}
                  onChange={e => setStartMonth(e.target.value)}
                  helperText="Month # from project start"
                  fullWidth
                />
                <TextField
                  label="Duration (months)"
                  type="number"
                  value={durationMonths}
                  onChange={e => setDurationMonths(e.target.value)}
                  fullWidth
                />
              </Stack>

              {/* Deliverables */}
              <SubEntitySection
                title="Deliverables"
                items={deliverables}
                showForm={showDeliverableForm}
                onToggleForm={() => { setShowDeliverableForm(true); setEditingDeliverableIndex(null); setDeliverableForm(emptyDeliverable()); }}
                onCancelForm={() => { setShowDeliverableForm(false); setEditingDeliverableIndex(null); setDeliverableForm(emptyDeliverable()); }}
                onAdd={handleAddDeliverable}
                canAdd={Boolean(deliverableForm.description?.trim())}
                isEditing={editingDeliverableIndex !== null}
                onDelete={i => {
                  const d = deliverables[i];
                  if (d?.id) deleteDeliverable(d.id);
                  setDeliverables(prev => prev.filter((_, idx) => idx !== i));
                }}
                onEdit={i => {
                  setDeliverableForm({ ...deliverables[i], taskId: deliverables[i].task || task?.id });
                  setEditingDeliverableIndex(i);
                  setShowDeliverableForm(true);
                }}
                renderItem={d => (
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" fontWeight="bold">{d.full_title || d.title || d.description}</Typography>
                    {d.type && <Chip label={d.type} size="small" color="primary" variant="outlined" />}
                    {d.status && <Chip label={d.status} size="small" />}
                    {d.delivery_month && <Typography variant="caption">M{d.delivery_month}</Typography>}
                  </Box>
                )}
                renderForm={
                  <Stack spacing={1.5}>
                    {/* Task reassignment — only shown when editing an existing item */}
                    {editingDeliverableIndex !== null && allTasks.length > 1 && (
                      <TaskReassignSelect
                        value={deliverableForm.taskId}
                        onChange={v => setDeliverableForm(p => ({ ...p, taskId: v }))}
                      />
                    )}
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select value={deliverableForm.type} onChange={e => setDeliverableForm(p => ({ ...p, type: e.target.value }))} label="Type" MenuProps={{ sx: { zIndex: 3001 } }}>
                        {[['R','Report'],['DEM','Demonstrator'],['DEC','Website'],['DATA','Dataset'],['DMP','Data Mgmt Plan'],['ETHICS','Ethics'],['SECURITY','Security'],['OTHER','Other']].map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="Title" size="small" fullWidth value={deliverableForm.title} onChange={e => setDeliverableForm(p => ({ ...p, title: e.target.value }))} />
                    <TextField label="Description *" size="small" fullWidth multiline rows={2} value={deliverableForm.description} onChange={e => setDeliverableForm(p => ({ ...p, description: e.target.value }))} />
                    <Stack direction="row" spacing={2}>
                      <TextField label="Delivery Month" type="number" size="small" fullWidth value={deliverableForm.delivery_month} onChange={e => setDeliverableForm(p => ({ ...p, delivery_month: e.target.value }))} />
                      <FormControl fullWidth size="small">
                        <InputLabel>Dissemination</InputLabel>
                        <Select value={deliverableForm.dissemination_level} onChange={e => setDeliverableForm(p => ({ ...p, dissemination_level: e.target.value }))} label="Dissemination" MenuProps={{ sx: { zIndex: 3001 } }}>
                          {[['PU','Public'],['SEN','Sensitive'],['Classified R-UE/EU-R','EU Restricted'],['other','Other']].map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Stack>
                }
              />

              {/* Objectives */}
              <SubEntitySection
                title="Objectives"
                items={objectives}
                showForm={showObjectiveForm}
                onToggleForm={() => { setShowObjectiveForm(true); setEditingObjectiveIndex(null); setObjectiveForm(emptyObjective()); }}
                onCancelForm={() => { setShowObjectiveForm(false); setEditingObjectiveIndex(null); }}
                onAdd={handleAddObjective}
                canAdd={Boolean(objectiveForm.description?.trim())}
                isEditing={editingObjectiveIndex !== null}
                onDelete={i => {
                  const o = objectives[i];
                  if (o?.id) deleteObjective(o.id);
                  setObjectives(prev => prev.filter((_, idx) => idx !== i));
                }}
                onEdit={i => {
                  setObjectiveForm({ ...objectives[i], taskId: objectives[i].task || task?.id });
                  setEditingObjectiveIndex(i);
                  setShowObjectiveForm(true);
                }}
                renderItem={o => <Typography variant="body2">{o.full_title || o.description}</Typography>}
                renderForm={
                  <Stack spacing={1.5}>
                    {editingObjectiveIndex !== null && allTasks.length > 1 && (
                      <TaskReassignSelect
                        value={objectiveForm.taskId}
                        onChange={v => setObjectiveForm(p => ({ ...p, taskId: v }))}
                      />
                    )}
                    <TextField label="Description *" size="small" fullWidth multiline rows={3} value={objectiveForm.description} onChange={e => setObjectiveForm(p => ({ ...p, description: e.target.value }))} />
                  </Stack>
                }
              />

              {/* Milestones */}
              <SubEntitySection
                title="Milestones"
                items={milestones}
                showForm={showMilestoneForm}
                onToggleForm={() => { setShowMilestoneForm(true); setEditingMilestoneIndex(null); setMilestoneForm(emptyMilestone()); }}
                onCancelForm={() => { setShowMilestoneForm(false); setEditingMilestoneIndex(null); }}
                onAdd={handleAddMilestone}
                canAdd={Boolean(milestoneForm.description?.trim())}
                isEditing={editingMilestoneIndex !== null}
                onDelete={i => {
                  const m = milestones[i];
                  if (m?.id) deleteMilestone(m.id);
                  setMilestones(prev => prev.filter((_, idx) => idx !== i));
                }}
                onEdit={i => {
                  setMilestoneForm({ ...milestones[i], taskId: milestones[i].task || task?.id });
                  setEditingMilestoneIndex(i);
                  setShowMilestoneForm(true);
                }}
                renderItem={m => (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{m.full_title || m.description}</Typography>
                    {m.due_month && <Chip label={`M${m.due_month}`} size="small" color="warning" />}
                  </Box>
                )}
                renderForm={
                  <Stack spacing={1.5}>
                    {editingMilestoneIndex !== null && allTasks.length > 1 && (
                      <TaskReassignSelect
                        value={milestoneForm.taskId}
                        onChange={v => setMilestoneForm(p => ({ ...p, taskId: v }))}
                      />
                    )}
                    <TextField label="Due Month" type="number" size="small" sx={{ width: 180 }} value={milestoneForm.due_month} onChange={e => setMilestoneForm(p => ({ ...p, due_month: e.target.value }))} helperText="Month # from project start" />
                    <TextField label="Description *" size="small" fullWidth multiline rows={2} value={milestoneForm.description} onChange={e => setMilestoneForm(p => ({ ...p, description: e.target.value }))} />
                    <TextField label="Means of Verification" size="small" fullWidth value={milestoneForm.verification_means} onChange={e => setMilestoneForm(p => ({ ...p, verification_means: e.target.value }))} />
                  </Stack>
                }
              />

              {/* Risks */}
              <SubEntitySection
                title="Risks"
                items={risks}
                showForm={showRiskForm}
                onToggleForm={() => { setShowRiskForm(true); setEditingRiskIndex(null); setRiskForm(emptyRisk()); }}
                onCancelForm={() => { setShowRiskForm(false); setEditingRiskIndex(null); }}
                onAdd={handleAddRisk}
                canAdd={Boolean(riskForm.description?.trim())}
                isEditing={editingRiskIndex !== null}
                onDelete={i => {
                  const r = risks[i];
                  if (r?.id) deleteRisk(r.id);
                  setRisks(prev => prev.filter((_, idx) => idx !== i));
                }}
                onEdit={i => {
                  setRiskForm({ ...risks[i], taskId: risks[i].task || task?.id });
                  setEditingRiskIndex(i);
                  setShowRiskForm(true);
                }}
                renderItem={r => (
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2">{r.full_title || r.description}</Typography>
                    <Chip label={`Impact: ${r.impact}`} size="small" color={r.impact === 'high' ? 'error' : r.impact === 'medium' ? 'warning' : 'success'} />
                    <Chip label={`Likelihood: ${r.likelihood}`} size="small" variant="outlined" />
                  </Box>
                )}
                renderForm={
                  <Stack spacing={1.5}>
                    {editingRiskIndex !== null && allTasks.length > 1 && (
                      <TaskReassignSelect
                        value={riskForm.taskId}
                        onChange={v => setRiskForm(p => ({ ...p, taskId: v }))}
                      />
                    )}
                    <TextField label="Description *" size="small" fullWidth multiline rows={2} value={riskForm.description} onChange={e => setRiskForm(p => ({ ...p, description: e.target.value }))} />
                    <Stack direction="row" spacing={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Impact</InputLabel>
                        <Select value={riskForm.impact} onChange={e => setRiskForm(p => ({ ...p, impact: e.target.value }))} label="Impact" MenuProps={{ sx: { zIndex: 3001 } }}>
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Likelihood</InputLabel>
                        <Select value={riskForm.likelihood} onChange={e => setRiskForm(p => ({ ...p, likelihood: e.target.value }))} label="Likelihood" MenuProps={{ sx: { zIndex: 3001 } }}>
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <TextField label="Mitigation Plan" size="small" fullWidth multiline rows={2} value={riskForm.mitigation_plan} onChange={e => setRiskForm(p => ({ ...p, mitigation_plan: e.target.value }))} />
                  </Stack>
                }
              />
            </>
          )}

          {error && <Alert severity="error">{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || !title.trim() || !workPackage}
        >
          {isSaving ? <CircularProgress size={20} /> : (isEdit ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Reusable inline sub-entity section ────────────────────────────────────────
const SubEntitySection = ({
  title, items, showForm, onToggleForm, onCancelForm,
  onAdd, canAdd, isEditing, onDelete, onEdit, renderItem, renderForm
}) => (
  <Box>
    <Divider sx={{ mb: 1.5 }} />
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      <Button size="small" startIcon={<AddIcon />} onClick={onToggleForm} disabled={showForm}>
        Add
      </Button>
    </Box>

    {showForm && (
      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
        {renderForm}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button size="small" onClick={onCancelForm}>Cancel</Button>
          <Button size="small" variant="contained" onClick={onAdd} disabled={!canAdd}>
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </Stack>
      </Box>
    )}

    {items.length > 0 ? (
      <List dense disablePadding>
        {items.map((item, i) => (
          <ListItem
            key={item.id || i}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.5 }}
            secondaryAction={
              <>
                <IconButton size="small" onClick={() => onEdit(i)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => onDelete(i)}><DeleteIcon fontSize="small" /></IconButton>
              </>
            }
          >
            <ListItemText primary={renderItem(item)} />
          </ListItem>
        ))}
      </List>
    ) : !showForm && (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No {title.toLowerCase()} yet.
      </Typography>
    )}
  </Box>
);

function emptyDeliverable() {
  return { assignee: null, type: 'R', title: '', description: '', delivery_month: '', dissemination_level: 'PU', status: 'not_started', taskId: null };
}
function emptyObjective() {
  return { description: '', taskId: null };
}
function emptyMilestone() {
  return { description: '', due_month: '', verification_means: '', taskId: null };
}
function emptyRisk() {
  return { description: '', impact: 'medium', likelihood: 'medium', mitigation_plan: '', taskId: null };
}

export default TaskDialog;
